import { Duration } from '@js-joda/core';

import { ShiftEntry, ShiftTimeEntry, ShiftType } from '#/shared/types/Timesheet';
import { formatDurationToHours, parseIsoOffsetZonedDateTimeToLocalTime } from '#/shared/utils/dateTimeHelper';
import { capitalizeFirstLetter } from '#/shared/utils/stringHelper';

import {
  ShiftDataEntry,
  ShiftEntryDuration,
  ShiftTimeEntryDurations,
  ShiftTypeLabel,
  TimesheetDayEntryData,
} from '../types/types';

export const mapShiftsToDayEntryList = (shifts: ShiftEntry[]): TimesheetDayEntryData[] => {
  return shifts.map(shiftData => {
    const shiftStartDate = shiftData.startAt;
    const shiftEntryData: ShiftDataEntry[] = shiftData.timeEntries.map(timeEntry => {
      const breakDuration = formatDurationToHours(timeEntry?.break);
      const totalDuration = formatDurationToHours(timeEntry.total);
      const notes = timeEntry.notes;

      return {
        type: getShiftTypeFromApiShiftType(timeEntry.shiftType),
        startTime: parseIsoOffsetZonedDateTimeToLocalTime(timeEntry.startAt)?.toString() ?? '',
        endTime: parseIsoOffsetZonedDateTimeToLocalTime(timeEntry.endAt)?.toString() ?? '',
        isNextDayEnding: timeEntry.startAt.dayOfMonth() !== timeEntry.endAt.dayOfMonth(),
        breakDuration,
        totalDuration,
        notes,
        paidBreakRequest: timeEntry.paidBreakRequest,
      };
    });

    return {
      shiftEntryData,
      shiftStartDate,
      day: shiftStartDate.dayOfMonth(),
      month: shiftStartDate.monthValue(),
      year: shiftStartDate.year(),
      weekDay: capitalizeFirstLetter(shiftStartDate.dayOfWeek().toString()).slice(0, 3),
    };
  });
};

export const getShiftTypeFromApiShiftType = (entryType: ShiftType): ShiftTypeLabel => {
  switch (entryType) {
    case 'Ordinary':
      return 'Ordinary';
    case 'On-Call':
      return 'On-call';
    case 'Callback':
      return 'Callback';
    default:
      throw new Error(`Unknown shift type: ${entryType}`);
  }
};

export const getShiftDurations = (
  data: ShiftEntry[],
  totalAcceptedBreakDuration: Duration,
): Record<ShiftType | 'break', string> => {
  const totalDurations: Record<ShiftType | 'break', Duration> = {
    Ordinary: Duration.ZERO,
    'On-Call': Duration.ZERO,
    Callback: Duration.ZERO,
    break: Duration.ZERO,
  };
  const totals = getShiftTotals(data);
  // Accumulate break durations; subtract accepted break exceptions
  totalDurations['break'] = totals.reduce((acc, entry) => acc.plusDuration(entry.break), Duration.ZERO);
  totalDurations['break'] = totalDurations['break'].minus(totalAcceptedBreakDuration);

  // Accumulate totals by shift type dynamically; add accepted break exceptions to ordinary
  const shiftTypes: ShiftType[] = ['Ordinary', 'On-Call', 'Callback'];
  for (const type of shiftTypes) {
    totalDurations[type] = totals.find(entry => entry.shiftType === type)?.total ?? Duration.ZERO;
    if (type === 'Ordinary') {
      totalDurations[type] = totalDurations[type].plusDuration(totalAcceptedBreakDuration);
    }
  }

  // Format and return
  return {
    Ordinary: formatDurationToHours(totalDurations['Ordinary']),
    'On-Call': formatDurationToHours(totalDurations['On-Call']),
    Callback: formatDurationToHours(totalDurations['Callback']),
    break: formatDurationToHours(totalDurations['break']),
  };
};

export const getShiftTotals = (data: ShiftEntry[]): ShiftTimeEntryDurations[] => {
  const timeEntries = data.flatMap(shift => shift.timeEntries);
  return getShiftTimeEntryDurationListFromShiftEntry(timeEntries);
};

export const getShiftTimeEntryDurationListFromShiftEntry = (
  shiftEntry: ShiftTimeEntry[],
): ShiftTimeEntryDurations[] => {
  const defaultTotal: ShiftEntryDuration = { total: Duration.ZERO, break: Duration.ZERO };
  const ordinaryDurations = getShiftTimeEntriesByShiftType(shiftEntry, 'Ordinary').reduce(
    calculateTotalDuration,
    defaultTotal,
  );
  const onCallDurations = getShiftTimeEntriesByShiftType(shiftEntry, 'On-Call').reduce(
    calculateTotalDuration,
    defaultTotal,
  );
  const callBackDurations = getShiftTimeEntriesByShiftType(shiftEntry, 'Callback').reduce(
    calculateTotalDuration,
    defaultTotal,
  );

  return [
    {
      total: ordinaryDurations.total,
      break: ordinaryDurations.break,
      shiftType: 'Ordinary',
    },
    {
      total: onCallDurations.total,
      break: onCallDurations.break,
      shiftType: 'On-Call',
    },
    {
      total: callBackDurations.total,
      break: callBackDurations.break,
      shiftType: 'Callback',
    },
  ];
};

export const calculateTotalDuration = (
  currentDuration: ShiftEntryDuration,
  shiftTimeEntry: ShiftTimeEntry,
): ShiftEntryDuration => {
  const totalDuration = shiftTimeEntry.total ?? Duration.ZERO;
  const breakDuration = shiftTimeEntry.break ?? Duration.ZERO;

  return {
    total: currentDuration.total.plusDuration(totalDuration),
    break: currentDuration.break.plusDuration(breakDuration),
  };
};

export const getShiftTimeEntriesByShiftType = (data: ShiftTimeEntry[], shiftType: ShiftType) => {
  return data.filter(entry => entry.shiftType === shiftType);
};
